/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import {
  CostMappingAlgorithm,
  Provider,
  ReservationsProvider,
  ReservationUtilizationTimePeriod,
  ResourceCostSplitRule,
  ResourceQuery,
  useReservationDetailsQuery,
  useReservationsAndMetadataQuery,
  useReservationUtilizationQuery,
  useResourceQuery,
} from "~/generated/graphql";
import { estateCategories } from "~/constants";
import {
  getAccountUrl,
  useCloudAccount,
  useConsoleUrl,
  useCurrency,
} from "~/hooks";
import { getProviderTooltip } from "~/hooks/useConsoleUrl/utils";
import { ACTIONS, useAccess } from "~/services/auth";
import { useCustomers } from "~/services/customers";
import {
  formatCostObject,
  formatMoney,
  inflect,
  isCloudResource,
  isCloudResourceWithExternalProvider,
  isCostResource,
  isNotNil,
  Maybe,
} from "~/tools";
import { NOT_AVAILABLE_TEXT } from "./constants";
import {
  mapCloudDetails,
  mapCostDetails,
  mapExternalIntegrationDetails,
} from "./mapping";
import { SavingsPlanProps } from "./types";
import {
  getResourceVisabilityDates,
  parseMetadata,
  reservationsDate,
  sortMetadata,
  textWithFallback,
} from "./utils";

// TODO: Consider splitting the file into smaller ones

export type Detail = {
  value: string;
  label: string;
};

const emptyData = {
  links: [],
  tags: [],
  details: [],
  metadata: [],
  metaTags: [],
  enrichments: undefined,
  cost: {
    forecast: "0",
    currentMonth: "0",
    previousMonth: "0",
    originalCost: {
      currentMonth: "0",
    },
    currentMonthLastSpendDate: "",
  },
  co2e: {
    forecast: "0",
    currentMonth: "0",
    previousMonth: "0",
  },
  createdAt: "",
  breadcrumbLabels: [],
  isExternal: false,
  savingsAmount: undefined,
  savingsAmountCount: undefined,
  savingsPlanData: undefined,
};

type EstateRecord = ResourceQuery["estateRecord"];

function getCloudAccount(
  data:
    | {
        estateRecord?:
          | {
              __typename?: "CloudResource";
              provider: Provider;
              cloudAccountId?: string | null;
            }
          | {
              __typename?: "CostResource";
              provider: Provider;
            }
          | null;
      }
    | undefined
) {
  const defaultValue = {
    id: "",
    provider: Provider.External,
  };

  if (data?.estateRecord == null) {
    return defaultValue;
  }

  return isCloudResource(data?.estateRecord)
    ? {
        id: data?.estateRecord.cloudAccountId ?? "",
        provider: data?.estateRecord.provider,
      }
    : defaultValue;
}

// Prevents display of details that linked to restricted pages for the user
function useDetailsParser() {
  const { hasAccess: canVisitAccountPage } = useAccess([
    ACTIONS.visitAccountDetails,
  ]);

  return (details: Detail[]) =>
    details.map((detail) => {
      if (detail.label === "Account") {
        return {
          ...detail,
          ...("target" in detail
            ? { target: canVisitAccountPage ? detail.target : null }
            : {}),
        };
      }

      return detail;
    });
}

export function useEstateDetails(nordcloudId: string) {
  const { isAdminLite } = useCustomers();
  const { currency } = useCurrency();
  const { getUrl } = useConsoleUrl();
  const detailsParser = useDetailsParser();

  const { data, error, loading, refetch } = useResourceQuery({
    variables: { id: nordcloudId, isLite: isAdminLite },
  });

  const { id: cloudAccountId, provider } = getCloudAccount(data);

  const cloudAccount = useCloudAccount(cloudAccountId, provider);

  if (data?.estateRecord == null) {
    return { data: { ...data, ...emptyData }, error, loading, refetch };
  }

  const estateRecord = data?.estateRecord;

  const isExternal = isCloudResourceWithExternalProvider(data.estateRecord);
  const typename =
    (isExternal ? estateRecord?.provider : estateRecord?.__typename) ?? "";

  const tags = estateCategoryTypenameToTags(typename);

  const estateDetails = detailsParser(
    isCloudResource(estateRecord)
      ? mapCloudDetails({
          ...estateRecord,
          ...getBaseCloudDetails(estateRecord),
          url: getUrl({
            id: estateRecord.id,
            name: estateRecord.name,
            ...getBaseCloudDetails(estateRecord),
          }),
          urlTooltip: getProviderTooltip(estateRecord.provider),
          savingsAmount: estateRecord.savingsAmount,
          cloudAccountUrl:
            cloudAccount?.url ?? getAccountUrl(cloudAccountId, "", provider),
          cloudAccountName: cloudAccount?.urlLabel ?? cloudAccountId,
        })
      : []
  );
  const ieDetails =
    isCloudResourceWithExternalProvider(estateRecord) &&
    mapExternalIntegrationDetails({
      ...estateRecord,
      ...getBaseCloudDetails(estateRecord),
    });

  const cloudDetails = ieDetails || estateDetails;

  const costDetails = isCostResource(estateRecord)
    ? mapCostDetails({
        ...estateRecord,
        providerType: estateRecord.providerType ?? "",
      })
    : [];

  const details = [...cloudDetails, ...costDetails];
  const metaObject = {
    ...estateRecord?.metadataAndTags,
    ...(isCloudResource(estateRecord) && {
      enrichments: estateRecord?.enrichments,
    }),
  };

  const metadataTagsEnrichments = mapMetadata(metaObject);

  const cost = {
    ...formatCostObject(estateRecord.cost ?? {}, currency),
  };
  const co2e = estateRecord.co2e;

  const breadcrumbLabels = [
    {
      value: nordcloudId,
      label: isCloudResource(estateRecord)
        ? estateRecord.name
        : estateRecord.providerType ?? "",
    },
  ];

  const links = getCloudEstateLinks(data.estateRecord);

  const awsType = estateDetails.find(
    (estate) => estate?.label.toLowerCase() === "aws type"
  )?.value;

  const savingsPlanData = getSavingsPlanDetails(metaObject, awsType);
  const resourceLifecycleDates = getResourceVisabilityDates(data?.estateRecord);

  return {
    data: {
      ...data,
      tags,
      details,
      ...metadataTagsEnrichments,
      cost,
      co2e,
      breadcrumbLabels,
      isExternal,
      links,
      savingsPlanData,
    },
    resourceLifecycleDates,
    error,
    loading,
    refetch,
  };
}

type SectionDetails = {
  instanceType: string;
  savingsPlansData: SavingsPlanProps;
  isReservedInstance: boolean;
  isReservationsAzure: boolean;
  skuName: string;
  nordcloudId: string;
};

export function useGetSectionDetails({
  instanceType,
  isReservationsAzure,
  isReservedInstance,
  savingsPlansData,
  skuName,
  nordcloudId,
}: SectionDetails) {
  const { monthlyCost } = useSavingsPlanCost(nordcloudId);

  switch (true) {
    case isReservedInstance:
      return { sectionText: "Instance Type", sectionValue: instanceType };
    case isReservationsAzure:
      return { sectionText: "SKU Name", sectionValue: skuName };
    case ["savingsplans", "sagemaker"].includes(savingsPlansData?.awsType):
      return {
        sectionText: "Saving Plan Type",
        sectionValue: savingsPlansData?.planType,
        paymentValue: savingsPlansData?.paymentOption,
        upFrontValue: Number(
          savingsPlansData?.upFront ?? NOT_AVAILABLE_TEXT
        ).toFixed(2),
        monthlyCost,
      };
    case savingsPlansData?.awsType?.includes("ec2instance"):
      return {
        sectionText: "Instance Family",
        sectionValue: savingsPlansData?.instanceFamily,
        paymentValue: savingsPlansData?.paymentOption,
        upFrontValue: Number(
          savingsPlansData?.upFront ?? NOT_AVAILABLE_TEXT
        ).toFixed(2),
        monthlyCost,
      };
    default:
      throw new Error("Could not retrieve section details.");
  }
}

function getCloudEstateLinks(estateRecord: EstateRecord) {
  if (!estateRecord) {
    return [];
  }
  return isCloudResource(estateRecord) ? estateRecord.links : [];
}

function getSavingsPlanDetails(input: Meta, awsType?: string) {
  const commitment = getSpecificMetadata(input, "commitment");
  const paymentOption = getSpecificMetadata(input, "paymentoption");
  const upFront = getSpecificMetadata(input, "upfrontpaymentamount");
  const startDate = getSpecificMetadata(input, "start");
  const endDate = getSpecificMetadata(input, "end");
  const term = yearsDiff(startDate as string, endDate as string);
  if (awsType?.includes("savingsplans/ec2instance")) {
    return {
      awsType: "ec2instance",
      instanceFamily: getSpecificMetadata(input, "ec2instancefamily"),
      commitment,
      paymentOption,
      upFront,
      term,
      startDate,
      endDate,
      monthly: "0.00",
    };
  }
  if (
    ["savingsplans/compute", "savingsplans/sagemaker"].includes(awsType ?? "")
  ) {
    return {
      awsType: "savingsplans",
      planType: "Compute",
      commitment,
      paymentOption,
      upFront,
      term,
      startDate,
      endDate,
      monthly: "0.00",
    };
  }
}

function getSpecificMetadata(input: Meta, field: string) {
  const { metadata } = mapMetadata(input);
  return metadata.find((data) => data.label.toLowerCase().includes(field))
    ?.value;
}

function yearsDiff(d1: string, d2: string) {
  const date1 = dayjs(d1).subtract(1, "month");
  const date2 = dayjs(d2);
  return date2.diff(date1, "year");
}

type Meta = {
  tags?:
    | {
        key: string;
        value: string;
      }[]
    | null;
  metadata?:
    | {
        key: string;
        value: string;
      }[]
    | null;
  enrichments?:
    | {
        integration: {
          id: string;
          name: string;
        };
        data?:
          | {
              key: string;
              value: string;
            }[]
          | null;
      }[]
    | null;
};

function mapMetadata(input?: Meta) {
  const metadata = input?.metadata ?? [];
  const metaTags = input?.tags ?? [];
  const enrichments = input?.enrichments ?? [];

  const sortedAndMappedMetadata = [...metadata]
    .sort(sortMetadata)
    .map(parseMetadata);

  const sortedAndMappedMetaTags = [...metaTags]
    .sort(sortMetadata)
    .map((meta) => ({
      label: meta.key,
      value: meta.value,
    }));

  const sortedAndMappedEnrichments =
    enrichments.map((enrichment) => ({
      externalIntegrationName: enrichment.integration?.name,
      externalIntegrationId: enrichment.integration?.id,
      data: [...(enrichment.data ?? [])].sort(sortMetadata).map(parseMetadata),
    })) ?? [];

  return {
    metadata: sortedAndMappedMetadata,
    metaTags: sortedAndMappedMetaTags,
    enrichments: sortedAndMappedEnrichments,
  };
}

function getBaseCloudDetails<
  T extends {
    region?: Maybe<string>;
    cloudAccountId?: Maybe<string>;
    providerType?: Maybe<string>;
    provider: Maybe<string>;
    providerId: Maybe<string>;
  },
>(estateRecord: T) {
  return {
    providerType: estateRecord.providerType ?? "",
    cloudAccountId: estateRecord.cloudAccountId ?? "",
    region: estateRecord.region ?? "",
    provider: estateRecord.provider ?? "",
    providerId: estateRecord.providerId ?? "",
  };
}

type UtilizationProps = {
  period: ReservationUtilizationTimePeriod;
  nordcloudId: string;
};

export function useGetUtilizationData({
  nordcloudId,
  period,
}: UtilizationProps) {
  const { data: reservationUtilizationDetail, loading } =
    useReservationUtilizationQuery({
      variables: {
        reservationId: nordcloudId,
        timePeriod: period,
      },
      fetchPolicy: "no-cache",
    });

  const monthlyUtilization =
    reservationUtilizationDetail?.reservationUtilization?.monthlyUtilization;
  const chartData = monthlyUtilization?.timeSeries ?? [];
  const utilization = monthlyUtilization?.utilization ?? "0";
  const benefit = monthlyUtilization?.benefit ?? "0";
  const forecast = monthlyUtilization?.forecast ?? "0";
  const utilizationValue = Number(utilization) * 100 ?? "";

  return {
    loading,
    chartData,
    benefit,
    forecast,
    utilizationValue,
  };
}

export function useAppsEnvsAlgorithmSelections(
  costSplitRules?: ResourceCostSplitRule
) {
  const [selectedApplications, setSelectedApplications] = React.useState<
    string[]
  >([]);

  const [selectedEnvironments, setSelectedEnvironments] = React.useState<
    string[]
  >(
    costSplitRules?.settings?.environments
      ?.map(({ id }) => id)
      .filter(isNotNil) ?? []
  );

  const [algorithm, setAlgorithm] = React.useState<
    CostMappingAlgorithm | undefined
  >(costSplitRules?.algorithm);

  const selectAppsAndEnvs = (appsValue: string[], envsValue: string[]) => {
    setSelectedApplications(appsValue);
    setSelectedEnvironments(envsValue);
    if (costSplitRules?.algorithm === undefined) {
      setAlgorithm(undefined);
    }
  };

  return {
    selectedApplications,
    selectedEnvironments,
    algorithm,
    setAlgorithm,
    selectAppsAndEnvs,
  };
}

type UseDetailsProps = {
  reservationDetails: Maybe<Reservations>;
  savingsPlanData: Maybe<Savings>;
  rate: number;
  currency: string;
  details: Detail[];
  isReservedInstance: boolean;
  isReservationsAzure: boolean;
  azureRIDetails: Maybe<ReservationsAndMetadataItem>;
};

type ReservationsAndMetadataItem = {
  originalQuantity?: Maybe<number>;
  from: string | undefined;
  to: string | undefined;
  expirationTimeInYears?: Maybe<string>;
};

type Reservations = {
  purchaseOption?: Maybe<string>;
  from: string | undefined;
  to: string | undefined;
};

type Savings = {
  commitment: Record<string, unknown> | string | undefined;
  term: number | undefined;
  startDate: Record<string, unknown> | string | undefined;
  endDate: Record<string, unknown> | string | undefined;
};

export function useDetails({
  reservationDetails,
  savingsPlanData,
  rate,
  currency,
  details,
  isReservedInstance,
  azureRIDetails,
}: UseDetailsProps) {
  const hasSavingsPlan = details.some(({ value }) =>
    value.includes("savingsplans")
  );

  const showInstanceDetails = isReservedInstance || hasSavingsPlan;

  const riDetails = [
    {
      label: "Quantity",
      value: `${azureRIDetails?.originalQuantity} (${azureRIDetails?.expirationTimeInYears})`,
    },
    {
      label: "Start Date",
      value: reservationsDate(azureRIDetails?.from),
    },
    {
      label: "End Date",
      value: reservationsDate(azureRIDetails?.to),
    },
  ];

  const purchaseDetails = [
    {
      label: "Purchase Option",
      value: textWithFallback(
        reservationDetails?.purchaseOption,
        NOT_AVAILABLE_TEXT
      ),
    },
    {
      label: "Start Date",
      value: reservationsDate(reservationDetails?.from),
    },
    {
      label: "End Date",
      value: reservationsDate(reservationDetails?.to),
    },
  ];

  const commitmentValue = formatMoney(
    (Number(savingsPlanData?.commitment) * rate).toFixed(2),
    currency,
    false
  );

  const year = savingsPlanData?.term;

  const savingsDetails = [
    {
      label: "Purchase commitment",
      value: commitmentValue
        ? `${commitmentValue}/hr (${year ?? 0} ${inflect("year")(year ?? 0)})`
        : NOT_AVAILABLE_TEXT,
    },
    {
      label: "Start Date",
      value: reservationsDate(savingsPlanData?.startDate?.toString()),
    },
    {
      label: "End Date",
      value: reservationsDate(savingsPlanData?.endDate?.toString()),
    },
  ];

  function getDetails(
    isReservedInstanceType: boolean,
    isAzureReservations: boolean
  ) {
    if (isAzureReservations) {
      return riDetails;
    }

    return isReservedInstanceType ? purchaseDetails : savingsDetails;
  }

  return { getDetails, showInstanceDetails };
}

export function useReservationsAzureDetails(nordcloudId: string) {
  const { isAdminLite } = useCustomers();
  const { data } = useReservationsAndMetadataQuery({
    variables: {
      provider: ReservationsProvider.Azure,
      limit: 1,
      filters: {
        reservationNid: nordcloudId,
      },
    },
    skip: isAdminLite,
  });

  const reservationsDetails = data?.reservationsAndMetadata?.reservations ?? [];

  const azureReservationDetails = reservationsDetails[0] ?? {};
  const billingPlan = textWithFallback(
    azureReservationDetails?.billingPlan,
    NOT_AVAILABLE_TEXT
  );
  const skuName = textWithFallback(
    azureReservationDetails?.skuName,
    NOT_AVAILABLE_TEXT
  );

  return {
    azureReservationDetails,
    billingPlan,
    skuName,
  };
}

function useSavingsPlanCost(nordcloudId: string) {
  const { data } = useReservationDetailsQuery({
    variables: {
      reservationId: nordcloudId,
    },
  });
  const monthlyCost =
    data?.reservationDetails?.monthlyCost ?? NOT_AVAILABLE_TEXT;

  return {
    monthlyCost,
  };
}

function estateCategoryTypenameToTags(typename: string) {
  const estateCategory = estateCategories[typename];
  return [
    {
      color: estateCategory?.color ?? "warning",
      text: estateCategory?.label ?? typename,
    },
  ];
}
